import { DestroyRef, Directive, type OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl, Validators, type ControlValueAccessor } from '@angular/forms';
import { koreanValidationMessage } from './validation-messages';

let nextFieldId = 0;

/**
 * 폼 컴포넌트 공통 CVA 베이스 — Reactive Forms(formControlName)와 [(ngModel)] 모두 호환.
 *
 * NG_VALUE_ACCESSOR provider 대신 생성자에서 ngControl.valueAccessor를 직접 지정한다
 * (순환 DI 없이 NgControl 상태 — touched·errors — 를 읽어 에러 메시지를 표시하기 위함).
 * 검증 에러는 한글 메시지로 필드 래퍼가 표시하며, 서버 400 message는 error 입력으로
 * 같은 자리에 표시한다 (표시 위치 이원화 방지).
 */
@Directive()
export abstract class PkFieldBase<T> implements ControlValueAccessor, OnInit {
  protected readonly ngControl = inject(NgControl, { optional: true, self: true });
  private readonly destroyRef = inject(DestroyRef);

  readonly label = input<string | null>(null);
  readonly hint = input<string | null>(null);
  /** 외부(서버 검증 등) 지정 에러 — 컨트롤 검증 에러보다 우선 표시 */
  readonly error = input<string | null>(null);
  /** 필수 표시(*) 강제 — 미지정 시 Validators.required 부착 여부로 자동 판단 */
  readonly required = input(false);

  /** label[for] 연결용 고유 id */
  readonly fieldId = `pk-field-${++nextFieldId}`;

  protected readonly value = signal<T>(this.defaultValue());
  readonly disabled = signal(false);

  /** 컨트롤 상태(touched·status) 변경 시 computed 재평가 트리거 */
  private readonly controlBump = signal(0);

  protected onChange: (value: T) => void = () => {};
  protected onTouched: () => void = () => {};

  constructor() {
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    const control = this.ngControl?.control;
    if (control != null) {
      // zoneless: markAsTouched/상태 변경은 CD를 유발하지 않으므로 control.events로 수동 트리거
      control.events
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.controlBump.update((v) => v + 1));
      this.controlBump.update((v) => v + 1);
    }
  }

  readonly isRequired = computed(() => {
    if (this.required()) {
      return true;
    }
    this.controlBump();
    return this.ngControl?.control?.hasValidator(Validators.required) ?? false;
  });

  readonly errorText = computed<string | null>(() => {
    const explicit = this.error();
    if (explicit !== null && explicit !== '') {
      return explicit;
    }
    this.controlBump();
    const control = this.ngControl?.control;
    if (control == null || !control.touched || control.valid || control.disabled) {
      return null;
    }
    return koreanValidationMessage(control.errors ?? {});
  });

  // ── ControlValueAccessor ──────────────────────
  writeValue(raw: unknown): void {
    this.value.set(this.coerceValue(raw));
  }

  registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /** 사용자 조작으로 값 변경 — 내부 signal 갱신 + 폼에 전파 */
  protected commit(value: T): void {
    this.value.set(value);
    this.onChange(value);
  }

  protected markTouched(): void {
    this.onTouched();
  }

  /** 초기값 (CVA writeValue 이전 상태) */
  protected abstract defaultValue(): T;

  /** writeValue로 들어온 임의 값을 T로 정규화 (reset(null) 방어) */
  protected abstract coerceValue(raw: unknown): T;
}
